import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/shadcn-ui/badge';
import { Button } from '@/components/shadcn-ui/button';
import { Input } from '@/components/shadcn-ui/input';
import { Separator } from '@/components/shadcn-ui/separator';
import { ScrollArea } from '@/components/shadcn-ui/scroll-area';
import {
  LayoutDashboard,
  FileText,
  FolderKanban,
  FilePlus,
  BarChart3,
  Users,
  Settings,
  Search,
  Bell,
  Menu,
  X,
  CheckSquare,
  UserPlus2Icon,
  UserSquare2,
  LogOut,
  FileChartColumnIncreasing,
  Key,
  Globe,
  KeyRound,
  MessageCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback } from '@radix-ui/react-avatar';
import { Toaster } from '@/components/shadcn-ui/sonner';
import { toast } from 'sonner';
import { useUserStore } from '@/stores/useUser.store';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn-ui/dialog';
import { Label } from '@/components/shadcn-ui/label';
import { adminAuthAxios } from '@/network/base/appAxios';

const AdminDashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const adminDetails = useUserStore(state => state.adminDetails);
  const { logoutAdmin } = useUserStore();

  const navigationItems = [
    {
      title: 'MAIN',
      visible: true,
      // visible: adminDetails?.superAdmin,
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', badge: null },
        { icon: FileText, label: 'Requests', path: '/admin/dashboard/requests', badge: null },
        { icon: FolderKanban, label: 'Projects', path: '/admin/dashboard/projects', badge: null },
        { icon: FilePlus, label: 'Automation Failures', path: '/admin/dashboard/automation-failures', badge: null },
        // { icon: FilePlus, label: 'New Requests', path: '/admin/dashboard/new-requests', badge: null },
        { icon: BarChart3, label: 'Analytics', path: '/admin/dashboard/analytics', badge: null },
        { icon: FileChartColumnIncreasing, label: 'Review Permits', path: '/admin/dashboard/review-permits', badge: null },
        { icon: MessageCircle, label: 'Guest Chat', path: '/admin/dashboard/guest-chat', badge: null },
        { icon: KeyRound, label: 'Creds Management', path: '/admin/dashboard/creds-management', badge: null },

      ],
    },
    // {
    //   title: 'MAIN',
    //   visible: !adminDetails?.superAdmin,
    //   items: [
    //     { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', badge: null },
    //     { icon: FileText, label: 'Review Permits', path: '/admin/dashboard/review-permits', badge: null },
    //   ],
    // },
    {
      title: 'ADMIN',
      visible: adminDetails?.superAdmin,
      items: [
        { icon: Users, label: 'Team', path: '/admin/dashboard/team', badge: null },
        { icon: Settings, label: 'Settings', path: '/admin/dashboard/settings', badge: null },
        { icon: UserSquare2, label: 'Admin Management', path: '/admin/dashboard/add-admin', badge: null },
        { icon: Globe, label: 'Ip Management', path: '/admin/dashboard/ip-management', badge: null },
      ],
    },
  ];

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/auth');
  };

  const handleResetPassword = async () => {
    if (!oldPassword || !newPassword) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await adminAuthAxios.post('/Auth/v1.0/reset-password', {
        email: adminDetails?.user_email,
        oldPassword,
        newPassword
      });
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setOldPassword('');
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 border-b border-border px-6">
        {/* <CheckSquare className="h-6 w-6" />  */}
        <Avatar className="size-8 bg-black rounded-md">
          <AvatarFallback>
            <img src="/logo.svg" alt="Logo" className="size-8 object-cover bg-black rounded-full p-1" />
          </AvatarFallback>
        </Avatar>
        <span className="text-base font-semibold">Zermit AI Admin</span>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-6">
          {navigationItems.filter(section => section.visible).map((section, idx) => (
            <div key={idx}>
              {section.title !== "MAIN" && <h3 className="mb-2 px-3 text-xs font-semibold text-muted-foreground">{section.title}</h3>}
              <div className="space-y-1">
                {section.items.map(item => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'dark bg-background-secondary text-foreground'
                            : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                        )
                      }
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge && (
                        <Badge variant="destructive" className="h-5 px-1.5">
                          {item.badge}
                        </Badge>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* User Profile */}
      <div className="border-t border-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex min-w-10 min-h-10 h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
            {adminDetails?.fullname?.split(' ').map(n => n[0]).join('').toUpperCase() || 'AD'}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium whitespace-nowrap max-w-24 truncate">{adminDetails?.fullname || 'Admin'}</p>
            <p className="text-xs text-muted-foreground">{adminDetails?.superAdmin ? 'Super Admin' : 'Admin'}</p>
          </div>
          <Button variant="ghost" title="Reset Password" size="icon" className="h-8 w-8" onClick={() => setResetPasswordOpen(true)}>
            <Key className="h-4 w-4" />
          </Button>
          <Button variant="ghost" title="Logout" size="icon" className="h-8 w-8" onClick={() => { handleLogout() }}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted w-full">
      <div className="flex h-screen overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden w-64 border-r border-border bg-card lg:block">
          <SidebarContent />
        </aside>

        {/* Mobile Sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 h-full w-64 border-r border-border bg-card">
              <SidebarContent />
            </aside>
          </div>
        )}

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Header */}
          <header className="flex h-16 items-center gap-4 border-b border-border bg-card px-6">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>

            <div className="flex flex-1 items-center gap-4">
              <div className="relative hidden w-full max-w-md sm:block">
                <h1 className="text-2xl font-bold font-baloo-2">Zermit AI Admin Dashboard</h1>
              </div>
            </div>


          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto bg-muted p-6">
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster />

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input value={adminDetails?.user_email || ''} disabled />
            </div>
            <div>
              <Label>Old Password</Label>
              <Input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
            </div>
            <div>
              <Label>New Password</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <Button onClick={handleResetPassword} disabled={loading} className="w-full">
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardLayout;
